import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Loading } from '../components/Shared';
import { apiFetch } from '../utils/api';

const STATUTS = [
  { val: '', label: 'Tous les statuts' },
  { val: 'recue', label: 'Reçue' },
  { val: 'en_cours', label: 'En cours' },
  { val: 'resolue', label: 'Résolue' },
  { val: 'cloturee', label: 'Clôturée' },
];

const STATUT_COLORS = {
  recue:    { bg: '#fef3c7', color: '#92400e', label: 'Reçue' },
  en_cours: { bg: '#dbeafe', color: '#1e40af', label: 'En cours' },
  resolue:  { bg: '#d1fae5', color: '#065f46', label: 'Résolue' },
  cloturee: { bg: '#f1f5f9', color: '#64748b', label: 'Clôturée' },
};

function StatutBadge({ statut }) {
  const s = STATUT_COLORS[statut] || { bg: '#f1f5f9', color: '#64748b', label: statut };
  return (
    <span style={{ padding: '0.2rem 0.6rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700,
      background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

export default function Reclamations() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState('');
  const [search, setSearch] = useState('');

  function load() {
    setLoading(true);
    const qs = filtre ? `?statut=${encodeURIComponent(filtre)}` : '';
    apiFetch(`/reclamations${qs}`)
      .then(d => { setList(d || []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(load, [filtre]);

  const filtered = search
    ? list.filter(r =>
        [r.reference, r.objet, r.nom_client, r.interlocuteur, r.intitule_formation]
          .some(v => v && v.toLowerCase().includes(search.toLowerCase()))
      )
    : list;

  async function handleCreate() {
    try {
      const rec = await apiFetch('/reclamations', {
        method: 'POST',
        body: JSON.stringify({ date_reclamation: new Date().toISOString().slice(0, 10) }),
      });
      navigate(`/reclamations/${rec.id}`);
    } catch (e) {
      alert('Erreur lors de la création : ' + e.message);
    }
  }

  const ouvertes = list.filter(r => r.statut !== 'cloturee').length;
  const enRetard = list.filter(r => r.statut !== 'cloturee' && Number(r.jours_ecoules) > 10).length;

  return (
    <div className="page-container">
      <PageHeader
        title="Réclamations"
        subtitle="Gestion des réclamations"
        action={<button className="btn-primary" onClick={handleCreate}>+ Nouvelle réclamation</button>}
      />

      {/* KPIs */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 140, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '0.8rem 1rem' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Ouvertes</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '0.2rem' }}>{ouvertes}</div>
        </div>
        <div style={{ flex: 1, minWidth: 140, background: enRetard > 0 ? '#fef2f2' : 'var(--bg-card)', border: `1px solid ${enRetard > 0 ? '#fca5a5' : 'var(--border)'}`, borderRadius: '10px', padding: '0.8rem 1rem' }}>
          <div style={{ fontSize: '0.75rem', color: enRetard > 0 ? '#b91c1c' : 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>En retard (&gt;10j)</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: enRetard > 0 ? '#dc2626' : 'var(--text-primary)', marginTop: '0.2rem' }}>{enRetard}</div>
        </div>
        <div style={{ flex: 1, minWidth: 140, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '0.8rem 1rem' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Total</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '0.2rem' }}>{list.length}</div>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={filtre}
          onChange={e => setFiltre(e.target.value)}
          style={{ padding: '0.4rem 0.7rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', cursor: 'pointer' }}>
          {STATUTS.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
        </select>
        <input
          type="text"
          placeholder="Rechercher…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '0.4rem 0.8rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', width: 220 }}
        />
      </div>

      {loading ? <Loading /> : (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
              Aucune réclamation{filtre ? ` avec le statut « ${STATUTS.find(s=>s.val===filtre)?.label} »` : ''}.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.87rem' }}>
              <thead>
                <tr style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
                  {['Référence', 'Date', 'Objet', 'Client / Session', 'Statut', 'Délai (j)', ''].map(h => (
                    <th key={h} style={{ padding: '0.6rem 0.8rem', textAlign: 'left', fontWeight: 700,
                      fontSize: '0.78rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.03em', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const enRetardRow = r.statut !== 'cloturee' && Number(r.jours_ecoules) > 10;
                  return (
                    <tr key={r.id}
                      style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-elevated)',
                        cursor: 'pointer', transition: 'background .15s' }}
                      onClick={() => navigate(`/reclamations/${r.id}`)}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'var(--bg-elevated)'}>
                      <td style={{ padding: '0.6rem 0.8rem', fontWeight: 700, color: 'var(--accent-blue)', whiteSpace: 'nowrap' }}>
                        {r.reference}
                      </td>
                      <td style={{ padding: '0.6rem 0.8rem', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                        {r.date_reclamation ? new Date(r.date_reclamation).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td style={{ padding: '0.6rem 0.8rem', maxWidth: 260 }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.objet || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                        </div>
                      </td>
                      <td style={{ padding: '0.6rem 0.8rem', color: 'var(--text-secondary)', maxWidth: 200 }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.nom_client || ''}
                          {r.intitule_formation ? <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}> — {r.intitule_formation}</span> : ''}
                        </div>
                      </td>
                      <td style={{ padding: '0.6rem 0.8rem', whiteSpace: 'nowrap' }}>
                        <StatutBadge statut={r.statut} />
                      </td>
                      <td style={{ padding: '0.6rem 0.8rem', whiteSpace: 'nowrap',
                        color: enRetardRow ? '#dc2626' : 'var(--text-secondary)', fontWeight: enRetardRow ? 700 : 400 }}>
                        {r.jours_ecoules != null ? `${r.jours_ecoules}j` : '—'}
                        {enRetardRow ? ' ⚠' : ''}
                      </td>
                      <td style={{ padding: '0.6rem 0.8rem', textAlign: 'right' }}>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '1rem' }}>›</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
